import { apiClient } from '@/lib/api';
import type {
  AnalysisResponse,
  CatalogHealthResponse,
  DiagnosisResponse,
  LinkedProductsResponse,
  MarketOverviewResponse,
  OutcomeData,
  ProductHistoryResponse,
  SignalsResponse,
  StrategyConfig,
  StrategyEffectivenessResponse,
  StrategyGroupsResponse,
  StrategySimulationsResponse,
} from '../types/smart-pricing.types';

export interface StrategicReportUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
  latencyMs: number;
}

export interface StrategicReportSummary {
  id: string;
  createdAt: string;
  title: string | null;
  lookbackDays: number;
  usage: StrategicReportUsage;
}

export interface StrategicReportData extends StrategicReportSummary {
  narrative: string;
}

export interface AiAnalysisResult {
  domain: string;
  narrative: string;
  selectedTools: string[];
  dataQuality: {
    hasRepricingData: boolean;
    hasCompetitorData: boolean;
    hasInventoryData: boolean;
    hasCostData: boolean;
    hasSalesData: boolean;
  };
  usage: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    costUsd: number;
    latencyMs: number;
  };
  generatedAt: string;
}

export const smartPricingApi = {
  analyze: async (productIds: string[]): Promise<AnalysisResponse> => {
    const result = await apiClient.post('/api/smart-pricing/analyze', { productIds });
    return result as unknown as AnalysisResponse;
  },

  simulateStrategies: async (
    productIds: string[],
    config?: StrategyConfig,
    useBasePrice?: boolean
  ): Promise<StrategySimulationsResponse> => {
    const result = await apiClient.post(
      '/api/smart-pricing/simulate-strategies',
      { productIds, config, ...(useBasePrice !== undefined && { useBasePrice }) },
      { timeout: 300000 } // 5 min para grupos grandes
    );
    return result as unknown as StrategySimulationsResponse;
  },

  // Revisión final del plan 3: `applyStrategy` (POST /apply-strategy) salió del
  // frontend —ninguna pantalla aplica en el acto: todo pasa por el asistente—.
  // El endpoint sigue vivo en el backend para la API y el MCP.

  getLinkedProducts: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<LinkedProductsResponse> => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.search) qs.set('search', params.search);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    const result = await apiClient.get(`/api/smart-pricing/linked-products${query}`, {
      timeout: 120000,
    });
    return result as unknown as LinkedProductsResponse;
  },

  unlinkProduct: async (productId: string): Promise<{ removed: number }> => {
    const result = await apiClient.delete(`/api/smart-pricing/linked-products/${productId}`);
    return result as unknown as { removed: number };
  },

  resetToBasePrice: async (productId: string): Promise<{ basePrice: number }> => {
    const result = await apiClient.post(
      `/api/smart-pricing/linked-products/${productId}/reset-to-base`,
      {}
    );
    return result as unknown as { basePrice: number };
  },

  resetAllToBasePrice: async (): Promise<{ updated: number }> => {
    const result = await apiClient.post(
      '/api/smart-pricing/linked-products/reset-all-to-base',
      {},
      { timeout: 120000 }
    );
    return result as unknown as { updated: number };
  },

  getProductHistory: async (productId: string): Promise<ProductHistoryResponse> => {
    const result = await apiClient.get(`/api/smart-pricing/linked-products/${productId}/history`);
    return result as unknown as ProductHistoryResponse;
  },

  getOutcome: async (logId: string, window: number): Promise<OutcomeData> => {
    const result = await apiClient.get(
      `/api/smart-pricing/history/${logId}/outcome?window=${window}`
    );
    return result as unknown as OutcomeData;
  },

  getStrategyEffectiveness: async (
    window: number,
    strategy?: string
  ): Promise<StrategyEffectivenessResponse> => {
    const qs = new URLSearchParams({ window: String(window) });
    if (strategy) qs.set('strategy', strategy);
    const result = await apiClient.get(
      `/api/smart-pricing/strategy-effectiveness?${qs.toString()}`
    );
    return result as unknown as StrategyEffectivenessResponse;
  },

  getMarketOverview: async (params?: {
    search?: string;
    category?: string;
    position?: string;
    page?: number;
    limit?: number;
  }): Promise<MarketOverviewResponse> => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.category) qs.set('category', params.category);
    if (params?.position) qs.set('position', params.position);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const result = await apiClient.get(`/api/smart-pricing/market-overview?${qs.toString()}`, {
      timeout: 120000,
    });
    return result as unknown as MarketOverviewResponse;
  },

  getStrategyGroups: async (groupBy?: string): Promise<StrategyGroupsResponse> => {
    const qs = groupBy ? `?groupBy=${groupBy}` : '';
    const result = await apiClient.get(`/api/smart-pricing/strategy-groups${qs}`, {
      timeout: 120000,
    });
    return result as unknown as StrategyGroupsResponse;
  },

  getCatalogDiagnosis: async (): Promise<DiagnosisResponse> => {
    const result = await apiClient.get('/api/smart-pricing/catalog-diagnosis');
    return result as unknown as DiagnosisResponse;
  },

  getCatalogHealth: async (): Promise<CatalogHealthResponse> => {
    const result = await apiClient.get('/api/smart-pricing/catalog-health', { timeout: 120000 });
    return result as unknown as CatalogHealthResponse;
  },

  // ── Informe Estratégico (Bloque B) ─────────────────────────────────────────

  generateStrategicReport: async (params?: {
    lookbackDays?: number;
  }): Promise<StrategicReportData> => {
    const result = await apiClient.post(
      '/api/smart-pricing/signals/strategic-report',
      params ?? {},
      { timeout: 120000 }
    );
    return result as unknown as StrategicReportData;
  },

  getLatestStrategicReport: async (): Promise<StrategicReportSummary | null> => {
    const result = await apiClient.get('/api/smart-pricing/signals/strategic-report/latest');
    return result as unknown as StrategicReportSummary | null;
  },

  getStrategicReportHistory: async (): Promise<StrategicReportSummary[]> => {
    const result = await apiClient.get('/api/smart-pricing/signals/strategic-report/history');
    return result as unknown as StrategicReportSummary[];
  },

  getStrategicReportById: async (id: string): Promise<StrategicReportData> => {
    const result = await apiClient.get(`/api/smart-pricing/signals/strategic-report/${id}`);
    return result as unknown as StrategicReportData;
  },

  postAiAnalysis: async (params: {
    domain: 'pricing' | 'inventory' | 'market' | 'demand';
    lookbackDays?: number;
  }): Promise<AiAnalysisResult> => {
    const result = await apiClient.post('/api/smart-pricing/signals/ai-analysis', params, {
      timeout: 120000,
    });
    return result as unknown as AiAnalysisResult;
  },

  getSignals: async (params?: { urgency?: string; type?: string }): Promise<SignalsResponse> => {
    const qs = new URLSearchParams();
    if (params?.urgency) qs.set('urgency', params.urgency);
    if (params?.type) qs.set('type', params.type);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    const result = await apiClient.get(`/api/smart-pricing/signals${query}`, {
      timeout: 120000,
    });
    return result as unknown as SignalsResponse;
  },
};
